import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import EngineSummaryCard from '../components/EngineSummaryCard.jsx';
import StrategyDiagnostics from '../components/StrategyDiagnostics.jsx';
import StrategyTradeHistory from '../components/StrategyTradeHistory.jsx';
import StrategyWinLossPanel from '../components/StrategyWinLossPanel.jsx';
import { fetchTradeHistory, fetchTradeStats } from '../utils/api.js';

function ReasoningPanel({ reason }) {
  return (
    <div className="rounded-2xl border border-gray-800 bg-gray-900/70 p-4" data-testid="reasoning-panel">
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-[0.65rem] uppercase tracking-wide text-gray-500">Thinker & Engine Logs</p>
          <h3 className="text-lg font-semibold text-white">Last Trade Reasoning</h3>
        </div>
      </div>
      <div className="text-sm text-gray-300">
        {reason ? reason : 'No reasoning captured yet.'}
      </div>
    </div>
  );
}

ReasoningPanel.propTypes = {
  reason: PropTypes.string,
};

export default function StrategyAnalytics({ prefetchedHistory, prefetchedStats }) {
  const [history, setHistory] = useState(prefetchedHistory || []);
  const [stats, setStats] = useState(prefetchedStats || null);
  const [loading, setLoading] = useState(!prefetchedHistory || !prefetchedStats);
  const [error, setError] = useState(null);
  const [sourceStatus, setSourceStatus] = useState({ trade_history: prefetchedHistory ? 'ok' : 'unknown', trade_stats: prefetchedStats ? 'ok' : 'unknown' });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const [histRes, statRes] = await Promise.all([fetchTradeHistory(), fetchTradeStats()]);
        if (!cancelled) {
          const nextHistory = histRes.ok ? (histRes.data?.data || histRes.data || []) : [];
          const nextStats = statRes.ok ? (statRes.data || {}) : null;
          if (histRes.ok) setHistory(nextHistory);
          if (statRes.ok) setStats(nextStats || {});
          setSourceStatus({
            trade_history: histRes.ok ? (nextHistory.length ? 'ok' : 'empty') : (histRes.status || 'unavailable'),
            trade_stats: statRes.ok ? (nextStats && Object.keys(nextStats).length ? 'ok' : 'empty') : (statRes.status || 'unavailable'),
          });
        }
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (!prefetchedHistory || !prefetchedStats) {
      load();
    }
    return () => {
      cancelled = true;
    };
  }, [prefetchedHistory, prefetchedStats]);

  const groupedTrades = useMemo(() => {
    const byEngine = new Map();
    history.forEach((trade) => {
      const key = (trade.engine || 'unknown').toLowerCase();
      if (!byEngine.has(key)) byEngine.set(key, []);
      byEngine.get(key).push(trade);
    });
    return byEngine;
  }, [history]);

  const engineCards = useMemo(
    () => [
      { name: 'Dream', key: 'dream' },
      { name: 'Scalping', key: 'scalping' },
      { name: 'Horizon', key: 'horizon' },
    ],
    [],
  );

  return (
    <div className="space-y-4" data-testid="strategy-analytics">
      {error && (
        <div className="text-xs text-red-300 bg-red-900/30 border border-red-800 rounded p-2">Failed to load analytics.</div>
      )}
      {loading && (
        <div className="text-xs text-gray-400" aria-live="polite">
          Loading analytics…
        </div>
      )}
      <div className="rounded border border-amber-800/70 bg-amber-950/20 p-3 text-xs text-amber-100" data-testid="strategy-raw-caveat">
        Strategy Analytics uses raw trade-log telemetry from non-canonical /api/trade_history and /api/trade_stats.
        This is not clean measurement evidence; open/raw rows are not exact closed outcomes.
        No profitability conclusion. Clean post-epoch exact closed samples remain insufficient unless /trade_summary readiness says otherwise.
      </div>
      <div className="flex flex-wrap gap-2 text-[0.65rem] uppercase tracking-wide text-gray-400" data-testid="strategy-source-status">
        <span className="rounded border border-gray-700 px-2 py-1">raw</span>
        <span className="rounded border border-gray-700 px-2 py-1">trade_history: {sourceStatus.trade_history || 'unknown'}</span>
        <span className="rounded border border-gray-700 px-2 py-1">trade_stats: {sourceStatus.trade_stats || 'unknown'}</span>
        <span className="rounded border border-amber-700 px-2 py-1 text-amber-200">insufficient samples</span>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {engineCards.map((eng) => (
          <EngineSummaryCard
            key={eng.key}
            engine={eng.name}
            stats={stats?.per_engine?.[eng.key] || {}}
            trades={(groupedTrades.get(eng.key) || []).slice(0, 6)}
            blockedReason={stats?.diagnostics?.find((d) => d.active)?.detail}
          />
        ))}
      </div>

      <StrategyWinLossPanel stats={stats} />

      <div className="grid lg:grid-cols-3 gap-4">
        <StrategyDiagnostics diagnostics={stats?.diagnostics || []} />
        <StrategyTradeHistory history={history} />
        <ReasoningPanel reason={stats?.last_trade_reasoning} />
      </div>
    </div>
  );
}

StrategyAnalytics.propTypes = {
  prefetchedHistory: PropTypes.array,
  prefetchedStats: PropTypes.object,
};
